// Probably need an ID gotten off the route, useParams mayhaps?

import { FormControlLabel, Grid, Paper, styled, Switch, Typography, useTheme } from "@mui/material";
import { GetFieldLookupItem, useFormMetadata, useLookups } from "@ngt/forms";
import { ChangeEvent, FC, useContext, useEffect, useMemo, useState } from "react";
import { Link, Outlet, useParams } from "react-router-dom";
import { ApiEnabledStatus, FormDefinitions, Permissions, StageSummary } from "../api/dtos";
import {
    DetailsContent,
    DetailsHeader,
    DetailsPanel,
    GridColumn,
    GridData,
    GridFillerRow,
    GridLabel,
    GridRow,
} from "../components/DetailsPanel";
import GridActions from "../components/GridActions";
import { StyledButton } from "../components/StyledButton";
import TitleBox from "../components/TitleBox";
import SetLinks from "../components/layout/SetLinks";
import CollapsibleAppTable from "../components/tables/CollapsibleAppTable";
import type { AppColumn, AppScalarValue } from "../components/tables/AppTable.types";
import { StyledLink } from "../components/utility/StyledLink";
import StageSummariesContext from "../contexts/StageSummariesContext";
import PermissionsContext from "../contexts/auth/PermissionsContext";
import ProjectContext from "../contexts/data/ProjectContext";
import { DefaultProjectCreateLink, DefaultStageCreateLink } from "../contexts/layouts/LinkContext";
import useProjectDetailsById from "../hooks/data/useProjectDetailsById";
import useStageSummariesByProjectId from "../hooks/data/useStageSummariesByProjectId";
import useApiActivityStatus from "../hooks/data/useApiActivityStatusByStageId";
import usePostApiStatusUpdate from "../hooks/data/usePostApiStatusUpdate";

const StyledPaper = styled(Paper)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const EMPTY_STAGE_SUMMARIES: readonly StageSummary[] = [];

const audFormatter = new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
});
const formatAUD = (value: number) => audFormatter.format(value);

function requiredStageId(row: StageSummary): number {
    if (row.id === undefined || row.id === null) {
        throw new Error("Stage summary is missing its stage ID.");
    }

    return row.id;
}

export function createStageColumns(
    projectId: number,
    permissions: readonly Permissions[] | undefined,
    statusLabel: (value: AppScalarValue) => string,
): AppColumn<StageSummary>[] {
    const unsold = {
        id: "unsold",
        label: "Unsold",
    };
    const sold = {
        id: "sold",
        label: "Sold",
    };
    const columns: AppColumn<StageSummary>[] = [
        {
            id: "name",
            label: "Stage Name",
            scalarType: "text",
            accessor: (row) => row.name,
            minWidth: 100,
            flex: 1,
            cell: ({ row, formatted }) => (
                <StyledLink to={`/projects/${projectId}/stages/${requiredStageId(row)}`}>{formatted}</StyledLink>
            ),
        },
        {
            id: "status",
            label: "Status",
            scalarType: "lookup",
            accessor: (row) => row.status,
            lookup: {
                labelFor: (value) => statusLabel(value),
            },
            sortAccessor: (row) => statusLabel(row.status),
            sortScalarType: "text",
            width: 120,
        },
        {
            id: "future",
            label: "Future Lots",
            scalarType: "number",
            accessor: (row) => row.future,
            align: "left",
            width: 100,
            aggregate: "sum",
        },
        {
            id: "unavailable",
            label: "Unavailable",
            scalarType: "number",
            accessor: (row) => row.unavailable,
            align: "left",
            width: 130,
            aggregate: "sum",
            groupHeader: unsold,
        },
        {
            id: "comingSoon",
            label: "Coming Soon",
            scalarType: "number",
            accessor: (row) => row.comingSoon,
            align: "left",
            width: 100,
            aggregate: "sum",
            groupHeader: unsold,
        },
        {
            id: "onHold",
            label: "On Hold",
            scalarType: "number",
            accessor: (row) => row.onHold,
            align: "left",
            width: 90,
            aggregate: "sum",
            groupHeader: unsold,
        },
        {
            id: "available",
            label: "Available",
            scalarType: "number",
            accessor: (row) => row.available,
            align: "left",
            width: 110,
            aggregate: "sum",
            groupHeader: unsold,
        },
        {
            id: "salesAdviceIssued",
            label: "Sales Advice Issued",
            scalarType: "number",
            accessor: (row) => row.salesAdviceIssued,
            align: "left",
            width: 100,
            aggregate: "sum",
            groupHeader: unsold,
        },
        {
            id: "contractIssued",
            label: "Contract Issued",
            scalarType: "number",
            accessor: (row) => row.contractIssued,
            align: "left",
            width: 110,
            aggregate: "sum",
            groupHeader: unsold,
        },
        {
            id: "contractExchanged",
            label: "Contract Exchanged",
            scalarType: "number",
            accessor: (row) => row.contractExchanged,
            align: "left",
            width: 120,
            aggregate: "sum",
            groupHeader: sold,
        },
        {
            id: "contractSettled",
            label: "Contract Settled",
            scalarType: "number",
            accessor: (row) => row.contractSettled,
            align: "left",
            width: 110,
            aggregate: "sum",
            groupHeader: sold,
        },
        {
            id: "totalLots",
            label: "Total Lots",
            scalarType: "number",
            accessor: (row) => row.totalLots,
            align: "left",
            width: 100,
            aggregate: "sum",
        },
        {
            id: "unsoldLotValue",
            label: "Value of Unsold Lots",
            scalarType: "number",
            accessor: (row) => row.unsoldLotValue,
            align: "left",
            flex: 1,
            aggregate: "sum",
            formatter: (value) => (typeof value === "number" ? formatAUD(value) : ""),
            aggregateFormatter: formatAUD,
        },
        {
            id: "exchangedLotValue",
            label: "Value of Exchanged Lots",
            scalarType: "number",
            accessor: (row) => row.exchangedLotValue,
            align: "left",
            flex: 1,
            aggregate: "sum",
            formatter: (value) => (typeof value === "number" ? formatAUD(value) : ""),
            aggregateFormatter: formatAUD,
        },
        {
            id: "settledLotValue",
            label: "Value of Settled Lots",
            scalarType: "number",
            accessor: (row) => row.settledLotValue,
            align: "left",
            flex: 1,
            aggregate: "sum",
            formatter: (value) => (typeof value === "number" ? formatAUD(value) : ""),
            aggregateFormatter: formatAUD,
        },
    ];

    if (
        permissions?.includes(Permissions.ProjectCanEditStage) ||
        permissions?.includes(Permissions.ProjectCanDeleteStage)
    ) {
        columns.push({
            id: "actions",
            label: "",
            headerLabel: "",
            kind: "action",
            scalarType: "text",
            accessor: (row) => String(row.id ?? ""),
            width: 85,
            cell: ({ row }) => (
                <GridActions
                    editPermission={Permissions.ProjectCanEditStage}
                    deletePermission={Permissions.ProjectCanDeleteStage}
                    editUrl={`/projects/${projectId}/stages/edit/${requiredStageId(row)}`}
                    deleteUrl={`/projects/${projectId}/stages/delete/${requiredStageId(row)}`}
                />
            ),
        });
    }

    return columns;
}

const useStageColumns = () => {
    const { data: permissions } = useContext(PermissionsContext);
    const metadata = useFormMetadata(FormDefinitions.Stage);
    const { data: lookups, loading: _lookupsLoading } = useLookups(metadata);
    const projectId = Number(useParams().projectId);

    return useMemo(
        () =>
            createStageColumns(projectId, permissions, (value) =>
                typeof value === "number"
                    ? (GetFieldLookupItem(lookups, "Status", value)?.value?.toString() ?? String(value))
                    : String(value ?? ""),
            ),
        [lookups, permissions, projectId],
    );
};

export const ProjectDetails: FC = () => {
    const projectMetadata = useFormMetadata(FormDefinitions.Project);

    const { data: permissions } = useContext(PermissionsContext);

    const theme = useTheme();

    const { data: projectLookups, loading: _lookupsLoading } = useLookups(projectMetadata);

    const params = useParams();

    const projectId = Number(params.projectId);

    const { data: projectApiStatus, refresh: refreshActivityStatus } = useApiActivityStatus(
        projectId,
        FormDefinitions.Project,
    );

    const [showArchived, setShowArchivedStages] = useState(false);

    useEffect(() => {
        if (permissions?.includes(Permissions.ProjectCanSeeArchivedStages)) {
            setShowArchivedStages(true);
        }
    }, [permissions]);

    const context = useStageSummariesByProjectId(projectId, showArchived);
    const { data: stageSummaries, loading: stagesLoading } = context;
    const { data: projectData, loading: _projectDataLoading } = useProjectDetailsById(projectId);

    const columns = useStageColumns();

    const { data: project } = useContext(ProjectContext);

    const exportMetadata = useMemo(
        () => ({
            exportTitle: project?.name,
            fileName: project?.name,
            includeDateTime: true,
        }),
        [project?.name],
    );

    const updateApiStatus = usePostApiStatusUpdate();

    const toggleArchived = () => {
        setShowArchivedStages((showArchived) => !showArchived);
    };

    const handleApiToggle = async (event: ChangeEvent<HTMLInputElement>) => {
        await updateApiStatus(projectId, FormDefinitions.Project, event.target.checked);
        refreshActivityStatus();
    };

    return (
        //Properties

        //Summary

        //StageList
        <StageSummariesContext.Provider value={context}>
            <Outlet />

            <SetLinks
                createProjectLink={DefaultProjectCreateLink}
                createStageLink={DefaultStageCreateLink}
                lotLinkEnabled={false}
            />
            <TitleBox>
                <Typography variant="h2">{projectData?.project?.name}</Typography>
                {permissions?.includes(Permissions.AdminAccess) && (
                    <Grid>
                        <FormControlLabel
                            label={
                                "Enable Api" +
                                (projectApiStatus == ApiEnabledStatus.Active ? ` - Id: ${projectId}` : "")
                            }
                            control={
                                <Switch
                                    checked={projectApiStatus == ApiEnabledStatus.Active}
                                    disabled={projectApiStatus == ApiEnabledStatus.Unused}
                                    onChange={handleApiToggle}
                                />
                            }
                        />
                    </Grid>
                )}
                <Grid container gap={theme.spacing(2)} justifyContent={"end"}>
                    {permissions?.includes(Permissions.ProjectsCanEditProject) && (
                        <StyledButton
                            variant="contained"
                            //@ts-expect-error
                            component={Link}
                            to={`/projects/${projectId}/edit`}
                            state={{
                                back: true,
                            }}
                        >
                            Edit Project
                        </StyledButton>
                    )}
                    {permissions?.includes(Permissions.ProjectCanEditCommissions) && (
                        <StyledButton
                            variant="contained"
                            //@ts-expect-error
                            component={Link}
                            to={`/projects/${projectId}/project-commissions/edit`}
                            state={{
                                back: true,
                            }}
                        >
                            Edit Commission
                        </StyledButton>
                    )}
                    {permissions?.includes(Permissions.ProjectCanEditPrecincts) && (
                        <StyledButton
                            variant="contained"
                            //@ts-expect-error
                            component={Link}
                            to={`/projects/${projectId}/project-precincts/edit`}
                            state={{
                                back: true,
                            }}
                        >
                            Edit Precincts
                        </StyledButton>
                    )}
                    {permissions?.includes(Permissions.ProjectCanAddStage) && (
                        <StyledButton
                            variant="contained"
                            //@ts-expect-error
                            component={Link}
                            to={`/projects/${projectId}/stages/create`}
                            state={{
                                back: true,
                            }}
                        >
                            Add Stage
                        </StyledButton>
                    )}
                </Grid>
            </TitleBox>

            <Grid
                container
                size={{
                    xs: 12,
                }}
                spacing={{
                    xs: 2,
                }}
                marginBottom={theme.spacing(2)}
            >
                <Grid
                    size={{
                        xs: 12,
                        lg: 6,
                    }}
                >
                    <DetailsPanel>
                        <DetailsHeader>Properties</DetailsHeader>
                        <DetailsContent>
                            <GridColumn
                                size={{
                                    xs: 12,
                                    md: 6,
                                }}
                            >
                                <GridRow>
                                    <GridLabel>Project Type:</GridLabel>
                                    <GridData>
                                        {GetFieldLookupItem(
                                            projectLookups,
                                            "Type",
                                            projectData?.project?.type,
                                        )?.value?.toString()}
                                    </GridData>
                                </GridRow>
                                <GridRow>
                                    <GridLabel>Project Status:</GridLabel>
                                    <GridData>
                                        {GetFieldLookupItem(
                                            projectLookups,
                                            "Status",
                                            projectData?.project?.status,
                                        )?.value?.toString()}
                                    </GridData>
                                </GridRow>
                                <GridRow>
                                    <GridLabel>Share:</GridLabel>
                                    <GridData>{projectData?.project?.percentShare}%</GridData>
                                </GridRow>
                                <GridRow>
                                    <GridLabel>Default Back on Market (Days):</GridLabel>
                                    <GridData>{projectData?.project?.defaultBackOnMarket}</GridData>
                                </GridRow>
                                <GridRow>
                                    <GridLabel>Margin Scheme:</GridLabel>
                                    <GridData>{projectData?.project?.marginScheme ? "Yes" : "No"}</GridData>
                                </GridRow>
                                <GridFillerRow />
                            </GridColumn>
                            <GridColumn
                                size={{
                                    xs: 12,
                                    md: 6,
                                }}
                            >
                                <GridRow>
                                    <GridLabel>Landowner:</GridLabel>
                                    <GridData>
                                        {GetFieldLookupItem(
                                            projectLookups,
                                            "Landowner",
                                            projectData?.project?.landowner,
                                        )?.value?.toString()}
                                    </GridData>
                                </GridRow>
                                <GridRow>
                                    <GridLabel>Developer:</GridLabel>
                                    <GridData>
                                        {GetFieldLookupItem(
                                            projectLookups,
                                            "Developer",
                                            projectData?.project?.developer,
                                        )?.value?.toString()}
                                    </GridData>
                                </GridRow>
                                <GridRow>
                                    <GridLabel>Developer Charge:</GridLabel>
                                    <GridData>{projectData?.project?.developerCharge}</GridData>
                                </GridRow>
                                <GridRow>
                                    <GridLabel>IA Commission:</GridLabel>
                                    <GridData>
                                        {projectData?.project?.iaCommission
                                            ? Intl.NumberFormat("en-AU", {
                                                  style: "currency",
                                                  currency: "AUD",
                                              }).format(projectData?.project?.iaCommission)
                                            : undefined}
                                    </GridData>
                                </GridRow>
                                <GridRow>
                                    <GridLabel>Landowner Per Lot Rate:</GridLabel>
                                    <GridData>
                                        {projectData?.project?.landownerPerLotRate
                                            ? Intl.NumberFormat("en-AU", {
                                                  style: "currency",
                                                  currency: "AUD",
                                              }).format(projectData?.project?.landownerPerLotRate)
                                            : undefined}
                                    </GridData>
                                </GridRow>
                                <GridFillerRow />
                            </GridColumn>
                        </DetailsContent>
                    </DetailsPanel>
                </Grid>
                <Grid
                    size={{
                        xs: 12,
                        lg: 6,
                    }}
                    sx={{
                        display: "flex",
                        alignItems: "stretch",
                    }}
                >
                    <DetailsPanel>
                        <DetailsHeader>Summary</DetailsHeader>
                        <DetailsContent>
                            <GridColumn
                                size={{
                                    xs: 12,
                                    md: 6,
                                }}
                            >
                                <GridRow>
                                    <GridLabel>Total Lots in Future Stages:</GridLabel>
                                    <GridData>{projectData?.futureStageLots}</GridData>
                                </GridRow>
                                <GridRow>
                                    <GridLabel>Total Lots in Current Stages:</GridLabel>
                                    <GridData>{projectData?.currentStageLots}</GridData>
                                </GridRow>
                                <GridFillerRow />
                            </GridColumn>
                            <GridColumn
                                size={{
                                    xs: 12,
                                    md: 6,
                                }}
                            >
                                <GridRow>
                                    <GridLabel>Value of Future Stages:</GridLabel>
                                    <GridData>
                                        {projectData?.futureStageValue
                                            ? Intl.NumberFormat("en-AU", {
                                                  style: "currency",
                                                  currency: "AUD",
                                              }).format(projectData?.futureStageValue)
                                            : undefined}
                                    </GridData>
                                </GridRow>
                                <GridRow>
                                    <GridLabel>Cost to Complete Future Stages:</GridLabel>
                                    <GridData>
                                        {projectData?.futureStageCompletionCost
                                            ? Intl.NumberFormat("en-AU", {
                                                  style: "currency",
                                                  currency: "AUD",
                                              }).format(projectData?.futureStageCompletionCost)
                                            : undefined}
                                    </GridData>
                                </GridRow>
                                <GridRow>
                                    <GridLabel>Value of Current Stages:</GridLabel>
                                    <GridData>
                                        {projectData?.currentStageValue
                                            ? Intl.NumberFormat("en-AU", {
                                                  style: "currency",
                                                  currency: "AUD",
                                              }).format(projectData?.currentStageValue)
                                            : undefined}
                                    </GridData>
                                </GridRow>
                                <GridFillerRow />
                            </GridColumn>
                        </DetailsContent>
                    </DetailsPanel>
                </Grid>
            </Grid>
            {permissions?.includes(Permissions.ProjectCanSeeArchivedStages) && (
                <FormControlLabel
                    label="Show Archived Stages"
                    control={<Switch checked={showArchived} onChange={toggleArchived} />}
                />
            )}
            <StyledPaper>
                <CollapsibleAppTable
                    density="compact"
                    startCollapsed={false}
                    title="Stages"
                    loading={stagesLoading}
                    columns={columns}
                    rows={stageSummaries ?? EMPTY_STAGE_SUMMARIES}
                    getRowId={requiredStageId}
                    columnControls={{
                        menu: true,
                    }}
                    filterControls
                    pagination={{}}
                    export={exportMetadata}
                />
            </StyledPaper>
        </StageSummariesContext.Provider>
    );
};
